import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import toolsData from './tools.json';
import categories from './categories.json';
import BucketLogo from './logo';
import Chatbot from "./chatbot";
import { MessageCircle } from "lucide-react";

// ===== Intro Video Modal Component =====
import IntroVideoModal from "./IntroVideoModal";
import IntroVideoPopup from "./IntroVideoPopup";

// ===== Rainy watery background =====
function RainBackground() {
  const drops = Array.from({ length: 40 }).map((_, i) => {
    const sizeClass = ['small', 'medium', 'large'][i % 3];
    const left = Math.random() * 100;
    return (
      <div
        key={i}
        className={`raindrop ${sizeClass}`}
        style={{ left: `${left}vw`, '--i': i }}
      />
    );
  });
  return <div className="rain-container">{drops}</div>;
}

// ===== Modal for tool details =====
function ToolModal({ tool, onClose }) {
  if (!tool) return null;
  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button
          aria-label="Close modal"
          className="modal-close"
          onClick={onClose}
        >
          &times;
        </button>
        <img src={tool.logo} alt={`${tool.name} logo`} />
        <h2>{tool.name}</h2>
        <p>{tool.description}</p>
        <p><strong>Capabilities:</strong> {tool.capabilities.join(', ')}</p>
        <a href={tool.url} target="_blank" rel="noopener noreferrer">
          Visit Website
        </a>
      </div>
    </div>
  );
}

// ===== Main App =====
function App() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [selectedTool, setSelectedTool] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // ===== Intro Video Popup State =====
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showIntroPopup, setShowIntroPopup] = useState(true); // always true

  // ===== Dark Mode Effect =====
  useEffect(() => {
    if (darkMode) document.body.classList.add('dark');
    else document.body.classList.remove('dark');
  }, [darkMode]);

  // ===== Filter tools by search + category =====
  const filteredTools = useMemo(() => {
    return toolsData.filter(tool => {
      const matchesFilter = filter ? tool.category === filter : true;
      const matchesSearch = search
        ? tool.name.toLowerCase().includes(search.toLowerCase()) ||
          tool.description.toLowerCase().includes(search.toLowerCase())
        : true;
      return matchesFilter && matchesSearch;
    });
  }, [search, filter]);

  return (
    <>
      {/* ===== Intro Video Popup (centered) ===== */}
      {showIntroPopup && (
        <IntroVideoPopup
          onOpen={() => {
            setShowIntroModal(true);
            setShowIntroPopup(false); // hide popup after user clicks
          }}
        />
      )}

      {/* ===== Intro Video Modal ===== */}
      <IntroVideoModal
        open={showIntroModal}
        onClose={() => setShowIntroModal(false)}
      />

      {/* ===== Rain Background ===== */}
      <RainBackground />

      {/* ===== Dark Mode Toggle ===== */}
      <button
        className="dark-toggle"
        onClick={() => setDarkMode(d => !d)}
        aria-label="Toggle dark mode"
      >
        {darkMode ? '☀️' : '🌙'}
      </button>

      {/* ===== Header with logo ===== */}
      <header className="app-header">
        <a href="/" className="logo-link">
          <BucketLogo width={48} height={48} />
          <span className="logo-title">Packet GPT</span>
        </a>
      </header>

      {/* ===== Sidebar ===== */}
      <aside className="sidebar">
        <h3>Categories</h3>
        <ul>
          <li>
            <button onClick={() => setFilter('')}>
              {categories.Default} All
            </button>
          </li>
          {Object.keys(categories).map(cat => (
            cat !== "Default" && (
              <li key={cat}>
                <button onClick={() => setFilter(cat)}>
                  {categories[cat]} {cat}
                </button>
              </li>
            )
          ))}
        </ul>
      </aside>

      {/* ===== Main Content ===== */}
      <main className="app-container">
        <input
          type="text"
          placeholder="Search tools..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search tools"
        />

        <div className="tool-list">
          {filteredTools.length === 0 ? (
            <p style={{ textAlign: 'center', width: '100%', fontStyle: 'italic' }}>
              No AI tools found.
            </p>
          ) : (
            filteredTools.map(tool => (
              <div
                key={tool.id}
                className="tool-card"
                onClick={() => setSelectedTool(tool)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') setSelectedTool(tool) }}
                aria-label={`Open details for ${tool.name}`}
              >
                <img src={tool.logo} alt={`${tool.name} logo`} />
                <h2>{tool.name}</h2>
                <p>{tool.description}</p>
                <p><strong>Capabilities:</strong> {tool.capabilities.join(', ')}</p>
                <a
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                >
                  Visit
                </a>
              </div>
            ))
          )}
        </div>
      </main>

      <ToolModal tool={selectedTool} onClose={() => setSelectedTool(null)} />

      {/* ===== Chatbot ===== */}
      <div className="chatbot-container">
        <button
          className="chatbot-toggle"
          onClick={() => setIsChatOpen(!isChatOpen)}
          aria-label="Toggle chatbot"
        >
          <MessageCircle size={24} />
        </button>

        {isChatOpen && (
          <div className="chatbot-popup">
            <Chatbot />
          </div>
        )}
      </div>
    </>
  );
}

export default App;
